import { useEffect, useRef, useState } from 'react';

export interface Presence {
  handle: string;
  displayName: string;
  panel: string;
}

export interface PatchedMessage {
  revision: number;
  ops: { path: string; value?: unknown; op?: string }[];
  by: string;
}

/**
 * The designer's live channel: presence and the patches other editors
 * applied, over a WebSocket at /ws/design/<code>. Without a WebSocket (or
 * when it drops) the designer keeps working and the caller polls.
 */
export function useDesignLive(
  code: string,
  enabled: boolean,
  onPatched: (m: PatchedMessage) => void,
) {
  const [editors, setEditors] = useState<Presence[]>([]);
  const [connected, setConnected] = useState(false);
  const socket = useRef<WebSocket | null>(null);
  const handler = useRef(onPatched);
  handler.current = onPatched;

  useEffect(() => {
    if (!enabled || typeof WebSocket === 'undefined') return;
    let ws: WebSocket;
    try {
      const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${scheme}://${window.location.host}/ws/design/${code}`);
    } catch {
      return;
    }
    socket.current = ws;
    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      setEditors([]);
    };
    ws.onerror = () => setConnected(false);
    ws.onmessage = (e: MessageEvent<string>) => {
      try {
        const m = JSON.parse(e.data) as { type: string } & Record<string, unknown>;
        if (m.type === 'presence') setEditors(m.editors as Presence[]);
        else if (m.type === 'patched') handler.current(m as unknown as PatchedMessage);
      } catch {
        // ignore a malformed frame
      }
    };
    return () => {
      socket.current = null;
      ws.close();
    };
  }, [code, enabled]);

  const focus = (panel: string) => {
    const ws = socket.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'focus', panel }));
  };

  return { editors, connected, focus };
}

/** Applies one patch op to a plain object tree in place, the way the platform does. */
export function applyOp(root: unknown, op: { path: string; value?: unknown; op?: string }): void {
  const parts = op.path
    .split('/')
    .slice(1)
    .map((p) => p.replace(/~1/g, '/').replace(/~0/g, '~'));
  if (parts.length === 0) return;
  let node: unknown = root;
  for (const part of parts.slice(0, -1)) {
    if (node === null || typeof node !== 'object') return;
    node = (node as Record<string, unknown>)[part];
  }
  const last = parts[parts.length - 1];
  if (Array.isArray(node)) {
    const index = last === '-' ? node.length : Number(last);
    if (op.op === 'remove') node.splice(index, 1);
    else if (index >= node.length) node.push(op.value);
    else node[index] = op.value;
  } else if (node && typeof node === 'object') {
    if (op.op === 'remove') delete (node as Record<string, unknown>)[last];
    else (node as Record<string, unknown>)[last] = op.value;
  }
}
